/**
 * Agenda da sala na banca (FE-57). O cronograma grava a hora de parede como
 * UTC (ver utils/scheduleDate.ts), então "agora" também é comparado em hora de
 * parede: o relógio do navegador é levado para a mesma escala.
 */

export interface IAgendaSchedule {
  slot_id: string;
  room_id: string;
  room_name: string;
  starts_at: string;
  ends_at: string;
}

export interface IAgendaItem {
  team_id: string;
  team_name: string;
  category: string;
  status: 'scored' | 'pending';
  schedule?: IAgendaSchedule | null;
}

export type TAgendaMark = 'now' | 'next' | null;

const time = (iso: string) => new Date(iso).getTime();

/** Relógio do navegador na escala de hora de parede em UTC do cronograma. */
export const wallClockNow = (now: number = Date.now()): number =>
  now - new Date(now).getTimezoneOffset() * 60_000;

/** Com horário primeiro, por início (e sala); depois os sem horário, por categoria e nome. */
export function sortByAgenda<T extends IAgendaItem>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    if (a.schedule && b.schedule) {
      return (
        time(a.schedule.starts_at) - time(b.schedule.starts_at) ||
        a.schedule.room_name.localeCompare(b.schedule.room_name)
      );
    }
    if (a.schedule) return -1;
    if (b.schedule) return 1;
    return (
      a.category.localeCompare(b.category) ||
      a.team_name.localeCompare(b.team_name)
    );
  });
}

/**
 * Marca, por sala, quem está com a banca agora (pelo horário) e a próxima
 * equipe pendente. Equipe já avaliada não é "a seguir".
 */
export function agendaMarks(
  items: IAgendaItem[],
  nowWall: number,
): Record<string, TAgendaMark> {
  const marks: Record<string, TAgendaMark> = {};
  const nextByRoom = new Map<string, IAgendaItem>();

  for (const item of sortByAgenda(items)) {
    const { schedule } = item;
    if (!schedule) continue;

    const start = time(schedule.starts_at);
    const end = time(schedule.ends_at);

    if (start <= nowWall && nowWall < end) {
      marks[item.team_id] = 'now';
    } else if (
      start >= nowWall &&
      item.status === 'pending' &&
      !nextByRoom.has(schedule.room_id)
    ) {
      nextByRoom.set(schedule.room_id, item);
      marks[item.team_id] = 'next';
    }
  }

  return marks;
}

/**
 * Equipe que a banca abre ao entrar: a que está na sala agora, se ainda
 * pendente; senão a próxima pendente da agenda; senão a primeira da lista.
 */
export function pickInitialTeam(
  items: IAgendaItem[],
  nowWall: number,
): string | null {
  const ordered = sortByAgenda(items);
  const marks = agendaMarks(ordered, nowWall);
  const pending = ordered.filter(item => item.status === 'pending');

  const current = pending.find(item => marks[item.team_id] === 'now');
  const next = pending.find(item => marks[item.team_id] === 'next');
  const upcoming = pending.find(
    item => !item.schedule || time(item.schedule.ends_at) > nowWall,
  );

  return (
    (current ?? next ?? upcoming ?? pending[0] ?? ordered[0])?.team_id ?? null
  );
}
